// Thin fetch wrapper that:
//  • prefixes VITE_API_URL when set (production), otherwise routes through
//    Vite's /api proxy in dev,
//  • injects Authorization: Bearer <token> from sessionStorage,
//  • normalises error bodies (always returns { error, message } on non-2xx),
//  • surfaces 401 via a dispatched 'auth:unauthorized' window event so the
//    AuthContext can react without coupling to react-router internals.

import { getStoredToken, getStoredSelectedTenant } from './tokenStore.js';

const RAW_BASE = import.meta.env.VITE_API_URL || '/api';
// Strip trailing slashes so we can concatenate with leading-slash paths cleanly.
const API_BASE = RAW_BASE.replace(/\/+$/, '');

function buildUrl(path) {
  if (!path.startsWith('/')) path = `/${path}`;
  return `${API_BASE}${path}`;
}

async function request(method, path, { body, headers, signal } = {}) {
  const token = getStoredToken();
  // Platform-owner tenant switch: when the owner has selected a tenant, send it
  // as X-Tenant-Id so the backend re-scopes this request. Ignored server-side
  // for non-owners, so it's harmless to always include when set.
  const selectedTenant = getStoredSelectedTenant();
  const finalHeaders = {
    Accept: 'application/json',
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(selectedTenant?.id ? { 'X-Tenant-Id': selectedTenant.id } : {}),
    ...headers
  };

  const res = await fetch(buildUrl(path), {
    method,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
    signal
  });

  // 204 No Content
  if (res.status === 204) return null;

  let data = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = await res.json().catch(() => null);
  } else {
    data = await res.text().catch(() => null);
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Notify AuthContext — it owns the redirect / state-clear.
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    const err = new Error(
      (data && (data.message || data.error)) || `HTTP ${res.status}`
    );
    err.status = res.status;
    err.body = data;
    throw err;
  }

  return data;
}

export const api = {
  get: (path, opts) => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put: (path, body, opts) => request('PUT', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  delete: (path, opts) => request('DELETE', path, opts)
};

// ─── Endpoint groups ─────────────────────────────────────────────────────────
// Names mirror v1 conventions to keep the call sites readable.

export const authAPI = {
  sendMagicLink: (email) => api.post('/auth/magic-link', { email }),
  verifyMagicLink: (token) => api.post('/auth/verify', { token })
};

// Platform-owner only (server enforces via OWNER_EMAILS → 403 otherwise).
export const adminAPI = {
  listTenants: () => api.get('/admin/tenants'),
  // Per-tenant provider API key overrides. Scoped to the tenant selected in the
  // switcher (X-Tenant-Id, sent automatically by request()). GET returns masked
  // state only (never plaintext); PUT sets (string) or clears (null) a key.
  getTenantApiKeys: () => api.get('/admin/tenant/api-keys'),
  updateTenantApiKeys: (payload) => api.put('/admin/tenant/api-keys', payload)
};

// Owner-only CUSTOMER PROVISIONING. Create a customer tenant + its first admin
// user, add/invite more users, list customers with their users, (re)send a
// magic-link invite, and deactivate/reactivate. Server enforces owner-only.
export const customersAPI = {
  list: () => api.get('/admin/customers'),
  create: (payload) => api.post('/admin/customers', payload),
  addUser: (tenantId, payload) => api.post(`/admin/customers/${tenantId}/users`, payload),
  invite: (tenantId, email) => api.post(`/admin/customers/${tenantId}/invite`, { email }),
  setStatus: (tenantId, status) => api.put(`/admin/customers/${tenantId}/status`, { status })
};

// Owner-only (#29) — retired-demo caller messages: numbers that may call a retired
// demo + the custom message the AI speaks to each. Platform-global.
export const demoCallersAPI = {
  list: () => api.get('/admin/demo-blocked-callers'),
  create: (payload) => api.post('/admin/demo-blocked-callers', payload),
  update: (id, payload) => api.put(`/admin/demo-blocked-callers/${id}`, payload),
  remove: (id) => api.delete(`/admin/demo-blocked-callers/${id}`)
};

export const meAPI = {
  // GET /me does not exist in core/ today — the dashboard treats POST /auth/verify
  // as the bootstrap path on hard refresh (re-verifies the saved magic-link token).
  // When core/ adds /me, swap this in without touching AuthContext.
  get: () => api.get('/me')
};

export const callsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/calls${query ? `?${query}` : ''}`);
  },
  get: (id) => api.get(`/calls/${id}`),
  conversation: (id) => api.get(`/calls/${id}/conversation`),
  // Re-derive the transcript FROM THE RECORDING (what was said, not what the AI generated).
  buildVerbatim: (id) => api.post(`/calls/${id}/verbatim`),
  // Fetch a call recording (Call.recordingUrl, e.g. "/admin/recordings/<stem>")
  // as a blob URL the <audio> element can play. The audio is served by an
  // authenticated core route that streams it from the VPS recording store, so a
  // bare <audio src> (which can't send the bearer token) would 401 — we fetch
  // with the auth + tenant headers here and hand back an object URL the caller
  // plays and later revokes. Keeps the token out of the URL entirely.
  //
  // Returns { url, mimeType, ext } so the player can decide whether the browser
  // can actually decode it. Recordings are .ogg/opus which Safari can't play —
  // the caller uses `mimeType`/`ext` to fall back to a download link there.
  // We derive an explicit audio MIME type (falling back to the response's own
  // Content-Type, then to audio/ogg) and re-wrap the blob with that type so the
  // <audio> element gets a definite type to probe with canPlayType().
  recordingBlobUrl: async (recordingUrl) => {
    const token = getStoredToken();
    const selectedTenant = getStoredSelectedTenant();
    const res = await fetch(buildUrl(recordingUrl), {
      headers: {
        // Accept a range of audio types — the store may serve ogg/opus, mp3,
        // wav, or webm depending on the egress config.
        Accept: 'audio/ogg, audio/mpeg, audio/wav, audio/webm, audio/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(selectedTenant?.id ? { 'X-Tenant-Id': selectedTenant.id } : {})
      },
      credentials: 'include'
    });
    if (!res.ok) {
      let msg = `Recording failed (${res.status})`;
      try { const j = await res.json(); msg = j.message || j.error || msg; } catch {}
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
    // Prefer the file extension in the URL (the store names files by stem +
    // ext), then the server's Content-Type, then a safe default. .ogg/.opus →
    // audio/ogg so <audio> knows to probe the Opus codec.
    const ext = (recordingUrl.match(/\.([a-z0-9]+)(?:\?|#|$)/i)?.[1] || '').toLowerCase();
    const EXT_MIME = {
      ogg: 'audio/ogg', opus: 'audio/ogg', oga: 'audio/ogg',
      mp3: 'audio/mpeg', wav: 'audio/wav', webm: 'audio/webm',
      m4a: 'audio/mp4', mp4: 'audio/mp4', aac: 'audio/aac'
    };
    const serverType = (res.headers.get('content-type') || '').split(';')[0].trim();
    const mimeType = EXT_MIME[ext]
      || (serverType.startsWith('audio/') ? serverType : '')
      || 'audio/ogg';
    const raw = await res.blob();
    // Re-wrap with an explicit audio type so canPlayType() has something to go on
    // (some servers return application/octet-stream, which <audio> can't probe).
    const blob = raw.type === mimeType ? raw : new Blob([raw], { type: mimeType });
    return { url: URL.createObjectURL(blob), mimeType, ext };
  },
  // ── Call feedback / learning loop (owner-only, tenant-scoped) ──
  // Annotate a call with a rating + coaching + tags; list a call's feedback; and
  // fetch the compiled coaching playbook block the outbound agent injects.
  listFeedback: (callId) => api.get(`/calls/${callId}/feedback`),
  addFeedback: (callId, payload) => api.post(`/calls/${callId}/feedback`, payload),
  coachingPlaybook: () => api.get('/tenant/coaching-playbook')
};

export const overviewAPI = {
  // Single endpoint that returns aggregate KPIs for the current month.
  // If core/ doesn't expose this yet, the dashboard renders zeros gracefully.
  metrics: () => api.get('/overview')
};

export const settingsAPI = {
  getAiConfig: () => api.get('/settings/ai-config'),
  updateAiConfig: (payload) => api.put('/settings/ai-config', payload),
  // Live engagement switch — { activeEngagement: 'mens-health' | 'vitamd-landing' }.
  setEngagement: (activeEngagement) => api.put('/settings/engagement', { activeEngagement }),
  listPhoneNumbers: () => api.get('/phone-numbers'),
  // Scheduled email call recaps — { enabled, email, timezone, times: ['HH:MM'] }.
  getEmailRecap: () => api.get('/settings/email-recap'),
  updateEmailRecap: (payload) => api.put('/settings/email-recap', payload),
  // The tenant's operating timezone — { timezone: 'America/Chicago' }. Storage
  // stays UTC; this is the zone the dashboard renders in and the zone a
  // wall-clock schedule is read in.
  getTimezone: () => api.get('/settings/timezone'),
  updateTimezone: (timezone) => api.put('/settings/timezone', { timezone })
};

export const faqsAPI = {
  // scope-aware: pass { campaignId, only } — see scopedListPath below (hoisted).
  list: (opts) => api.get(scopedListPath('/faqs', opts)),
  create: (payload) => api.post('/faqs', payload),
  update: (id, payload) => api.put(`/faqs/${id}`, payload),
  remove: (id) => api.delete(`/faqs/${id}`)
};

// Personas — the panel-voice ROSTER (how many AI voices answer + each one's
// voice & display name). The panel engine turns on at 2+ rows; what they SAY
// lives in panelInstructions ("Panel flow" in Settings).
export const voicesAPI = {
  list: () => api.get('/voices'),
  available: () => api.get('/voices/available'),
  // Vendor-aware catalog spanning ElevenLabs + Deepgram (Aura) — the single
  // source the Personas editor and campaign Voice field both pick from.
  catalog: () => api.get('/voices/catalog'),
  create: (payload) => api.post('/voices', payload),
  update: (id, payload) => api.put(`/voices/${id}`, payload),
  remove: (id) => api.delete(`/voices/${id}`),
  // Fetch a voice sample for the "listen" button as a blob URL. We fetch with the
  // auth headers (the api wrapper only parses JSON/text, and a bare <audio src>
  // can't send an Authorization header), then hand back an object URL the caller
  // plays and later revokes. Keeps the bearer token out of URLs entirely.
  previewBlobUrl: async (provider, voiceId) => {
    const token = getStoredToken();
    const selectedTenant = getStoredSelectedTenant();
    const params = new URLSearchParams({ provider, voiceId });
    const res = await fetch(buildUrl(`/voices/preview?${params.toString()}`), {
      headers: {
        // Most vendors return MP3; xAI can only return PCM, which core wraps as WAV. Ask
        // for any audio rather than naming one container.
        Accept: 'audio/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(selectedTenant?.id ? { 'X-Tenant-Id': selectedTenant.id } : {})
      },
      credentials: 'include'
    });
    if (!res.ok) {
      let msg = `Preview failed (${res.status})`;
      try { const j = await res.json(); msg = j.message || j.error || msg; } catch {}
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }
};

// scope-aware list: pass { campaignId, only } to filter.
//   no opts          → tenant-wide rows (campaignId IS NULL)
//   { campaignId }    → that campaign's rows + tenant-wide
//   { campaignId, only:true } → ONLY that campaign's rows
function scopedListPath(base, { campaignId, only } = {}) {
  if (!campaignId) return base;
  const q = new URLSearchParams({ campaignId });
  if (only) q.set('scope', 'only');
  return `${base}?${q.toString()}`;
}

// ── PITCHES ──────────────────────────────────────────────────────────────────
// A pitch is the WHOLE bundle — body + behaviorBlock + dtmfRules + openingLine —
// so selecting one swaps all four atomically. Four fields edited one at a time is
// what let `Carriers — Houston` run a process-first body while behaviorBlock still
// carried the old SMB pitch's "don't chase a decision-maker", directly contradicting
// a pitch built on asking for the driver desk.
//
// Contract: core/src/routes/pitches.js (`present()` is the row shape).
//   GET    /pitches ?includeArchived=1   → { data: [Pitch] }
//   POST   /pitches/:id/clone            → 201 { data: Pitch }   body { name?, notes? }
//   PUT    /pitches/:id                  → { data: Pitch, versionChanged: true }
//   DELETE /pitches/:id                  → { data:{archived,id} } | 409 pitch_in_use
//   PUT    /campaigns/:id/pitch          → { data:{ pitchId, pitchName, version, defines, liveNow } }
//                                          409 pitch_archived · 404 pitch_not_found
// 404 on the LIST = not deployed yet; every surface hides itself.
export const pitchesAPI = {
  list: (includeArchived) => api.get(`/pitches${includeArchived ? '?includeArchived=1' : ''}`),
  get: (id) => api.get(`/pitches/${id}`),
  create: (payload) => api.post('/pitches', payload),
  clone: (id, payload = {}) => api.post(`/pitches/${id}/clone`, payload),
  update: (id, payload) => api.put(`/pitches/${id}`, payload),
  archive: (id) => api.delete(`/pitches/${id}`),
  selectForCampaign: (campaignId, pitchId) =>
    api.put(`/campaigns/${campaignId}/pitch`, { pitchId })
};

export const knowledgeAPI = {
  list: (opts) => api.get(scopedListPath('/knowledge', opts)),
  create: (payload) => api.post('/knowledge', payload),
  update: (id, payload) => api.put(`/knowledge/${id}`, payload),
  remove: (id) => api.delete(`/knowledge/${id}`)
};

export const routingAPI = {
  get: () => api.get('/routing'),
  update: (payload) => api.put('/routing', payload)
};

// CALL TRANSFER DIRECTORY — the people the AI can hand a LIVE caller to
// (`transfer_targets`). Distinct from routingAPI's `forwarding.contacts`, which is
// the older jambonz-era webhook branch and is NOT what the panel reads: the panel
// queries transfer_targets directly (panel_agent_multitenant.py:2989) and matches
// callers to a row on name / department / keywords.
export const transferTargetsAPI = {
  list: () => api.get('/transfer-targets'),
  create: (payload) => api.post('/transfer-targets', payload),
  update: (id, payload) => api.put(`/transfer-targets/${id}`, payload),
  remove: (id) => api.delete(`/transfer-targets/${id}`)
};

// Tenant documents & notes — the admin's reference material the AI uses on calls.
// `area` files each row under a dashboard pillar/section (call-routing | sales |
// employee | employee:communication | employee:training | employee:onboarding).
// For a 'document' kind the dashboard extracts the file text CLIENT-SIDE and
// sends it as `content`; for a 'note' the admin types `content` directly.
export const documentsAPI = {
  list: (area, expertContextId) => {
    const q = new URLSearchParams();
    if (area) q.set('area', area);
    if (expertContextId) q.set('expertContextId', expertContextId);
    const s = q.toString();
    return api.get(`/documents${s ? `?${s}` : ''}`);
  },
  create: (payload) => api.post('/documents', payload),
  update: (id, payload) => api.put(`/documents/${id}`, payload),
  remove: (id) => api.delete(`/documents/${id}`)
};

export const phoneNumbersAPI = {
  list: () => api.get('/phone-numbers'),
  update: (id, payload) => api.put(`/phone-numbers/${id}`, payload)
};

// LINE READINESS — "will this number ring, and if not, why?" (core:
// src/routes/readiness.js). Read-only: DB selects plus one LiveKit and one
// Telnyx GET. `sweep` is owner-only and is O(1) in vendor calls no matter how
// many tenants it covers, so it is safe to call for the whole customer list.
// A 404 from any of these means core doesn't have the routes deployed yet —
// call sites treat that as "no verdict", never as a dead line.
//
// ⚠️ `sweep({ did })` for a number NOBODY owns answers a DIFFERENT shape:
// `{ did, tenants: [], orphanDid: true, checkedAt }` — no liveCount, no
// blockedCount. Anything reading those two fields must handle it (this is the
// "is this number free to claim?" answer the provisioning flow will want).
// LINE PROVISIONING — the one surface that mutates Telnyx AND LiveKit.
// `provision` defaults to a DRY RUN server-side; callers must pass confirm:true
// deliberately. Owner-only. See core/src/services/lineProvisionService.js.
// DEAD-AUDIO caller flagging. The panel warns + ends a call it can't hear and
// reports the incident; a SECOND incident auto-blocks the number at call-start
// "pending admin review". These are that review. Tenant-scoped, authMiddleware.
// PUT status:'cleared' also resets incidentCount server-side.
export const flaggedCallersAPI = {
  list: () => api.get('/admin/flagged-callers'),
  review: (id, payload) => api.put(`/admin/flagged-callers/${id}`, payload),
  remove: (id) => api.delete(`/admin/flagged-callers/${id}`)
};

// CARE LINE — scheduled well-being calls for a family-care tenant (Gary's Knoxville
// line). Not a campaign: no dialer, no DNC scrubbing, no QA grading. Retries are
// CAPPED and end in a human being emailed — see docs/care-line-retry-spec-2026-07-30.md.
// 404 = core hasn't shipped it; the card hides itself.
export const careLineAPI = {
  get: (tenantId) => api.get(`/tenants/${tenantId}/care-line`),
  update: (tenantId, payload) => api.put(`/tenants/${tenantId}/care-line`, payload)
};

export const provisioningAPI = {
  targets: () => api.get('/admin/provision-targets'),
  provision: (payload) => api.post('/admin/provision-line', payload),
  // REASSIGN a number to another tenant. Dry run unless confirm:true, and returns a
  // per-layer plan across the four things that must agree: the phone_numbers row,
  // the Telnyx connection, the LiveKit trunk, and the box's own env gates. A 404
  // means core hasn't shipped it yet and the UI hides itself.
  reassign: (payload) => api.post('/admin/reassign-number', payload)
};

export const readinessAPI = {
  mine: (did) => api.get(`/readiness${did ? `?did=${encodeURIComponent(did)}` : ''}`),
  forTenant: (tenantId, did) =>
    api.get(`/tenants/${tenantId}/readiness${did ? `?did=${encodeURIComponent(did)}` : ''}`),
  sweep: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.did) qs.set('did', params.did);
    if (params.tenantId) qs.set('tenantId', params.tenantId);
    const q = qs.toString();
    return api.get(`/admin/line-readiness${q ? `?${q}` : ''}`);
  }
};

// DTMF access codes (346 "punch a code at call open" feature). Tenant-scoped CRUD.
export const accessCodesAPI = {
  list: () => api.get('/access-codes'),
  create: (payload) => api.post('/access-codes', payload),
  update: (id, payload) => api.put(`/access-codes/${id}`, payload),
  remove: (id) => api.delete(`/access-codes/${id}`)
};

// Knowledge branches — the on-demand lookup corpus (KnowledgeTopic rows). Each branch
// has a topicId (stable key + sort order), a title (the AI's menu label), and content
// the AI pulls when a caller's question matches. PUT upserts by topicId; DELETE by row id.
export const knowledgeBranchesAPI = {
  list: () => api.get('/admin/knowledge-topics'),
  upsert: (payload) => api.put('/admin/knowledge-topics', payload),
  remove: (id) => api.delete(`/admin/knowledge-topics/${id}`)
};

// Business information — the per-tenant company details the AI uses on every
// call (name, website, phone, who to reach, hours, address, notes). One row per
// tenant; PUT upserts.
export const businessInfoAPI = {
  get: () => api.get('/business-info'),
  update: (payload) => api.put('/business-info', payload)
};

// Demonstrations — LIGHTWEIGHT, short-lived inbound demos (NOT full tenants):
// each has its own number + 1–3 AIs + a primed prompt + opening + an editable
// "expires in N days". Tenant-scoped CRUD; list rows carry computed expiry
// (isExpired / effectiveStatus / daysLeft).
export const demonstrationsAPI = {
  list: () => api.get('/demonstrations'),
  create: (payload) => api.post('/demonstrations', payload),
  update: (id, payload) => api.put(`/demonstrations/${id}`, payload),
  remove: (id) => api.delete(`/demonstrations/${id}`)
};

// INTERNAL pricing / cost calculator — owner-only (server enforces via
// OWNER_EMAILS → 403 otherwise). Returns { data, computed } where `computed` is
// the server's per-minute cost breakdown + suggested packages.
export const pricingAPI = {
  get: () => api.get('/pricing-config'),
  update: (payload) => api.put('/pricing-config', payload)
};

export const outboundAPI = {
  place: (payload) => api.post('/calls/place', payload)
};

// Executive-Admin TASK dashboard — the chief-of-staff's work, bucketed
// completed / pending / not-completed(+reason). Unifies call_tasks (deliver-a-
// message callouts) + research_tasks (search-then-call). Read-only.
export const tasksAPI = {
  list: () => api.get('/tasks'),
  get: (id) => api.get(`/tasks/${id}`),
  // Chat task-intake — send the conversation, get the assistant's reply (+ any
  // tasks it created). { messages: [{role:'user'|'assistant', content}] }.
  chat: (messages) => api.post('/assistant/chat', { messages })
};

// Executive-Admin personal CONTACT DIRECTORY (name → number). Backs "call John
// and tell him…" outbound resolution; seeded on the onboarding call + editable here.
export const contactsAPI = {
  list: (q) => api.get(`/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  create: (payload) => api.post('/contacts', payload),
  update: (id, payload) => api.put(`/contacts/${id}`, payload),
  remove: (id) => api.delete(`/contacts/${id}`)
};

// Executive-Admin CONFERENCE CALLS — Zoom credentials + the merge/conference call
// history with the assistant's extracted context. Backend built by the Charlie
// session (contract in docs/channel-charlie-delta.md); these degrade to empty /
// not-configured while the endpoints 404.
export const zoomCredentialsAPI = {
  // GET → { connected, accountId?, clientId?, updatedAt? } (never the secret).
  // Unwrap the { data } envelope like googleCalendarAPI.status() below — the Zoom
  // card reads these fields off the top level, so without this the card always
  // shows "disconnected" even after a successful save.
  get: () => api.get('/integrations/zoom').then((r) => r?.data || r),
  // PUT sets/replaces the S2S creds — ALL THREE required (accountId, clientId, clientSecret).
  update: (payload) => api.put('/integrations/zoom', payload),
  // DELETE clears the tenant's Zoom connection.
  remove: () => api.delete('/integrations/zoom')
};

// Google Calendar — an OAuth CONNECT flow (not a creds form). Backend built by the
// Charlie session (core SHA e889278, migration 0046); see
// docs/handoff-delta-calendar-connect.md. Responses are { data: {...} }-wrapped.
export const googleCalendarAPI = {
  // GET → { connected, accountEmail?, calendarId?, configured }
  status: () => api.get('/integrations/google-calendar').then((r) => r?.data || r),
  // GET (admin) → { url } — the Google consent URL to open.
  connectUrl: () => api.get('/integrations/google-calendar/connect').then((r) => r?.data || r),
  // DELETE (admin) → { connected:false }
  remove: () => api.delete('/integrations/google-calendar')
};

// Cal.com — an API-KEY integration (not OAuth). The AI books through the same
// calendar seam as Google; connecting Cal.com replaces Google for that tenant.
// The API key is write-only (stored encrypted server-side, never returned).
export const calcomAPI = {
  // GET → { connected, eventTypeId?, username?, configured }
  status: () => api.get('/integrations/calcom').then((r) => r?.data || r),
  // PUT (admin) → { connected, eventTypeId, username } — { apiKey, eventTypeId, username? }
  update: (payload) => api.put('/integrations/calcom', payload).then((r) => r?.data || r),
  // DELETE (admin) → { connected:false }
  remove: () => api.delete('/integrations/calcom')
};

export const conferenceCallsAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/conference-calls${q ? `?${q}` : ''}`);
  },
  get: (id) => api.get(`/conference-calls/${id}`)
};

// Executive-Admin "Expert Mode" — the named EXPERT CONTEXTS the owner authors and
// invokes by voice ("be the expert on <name>"). Tenant-scoped CRUD; the panel
// resolves the spoken subject server-side via /internal/expert-context.
export const expertContextsAPI = {
  list: () => api.get('/expert-contexts'),
  create: (payload) => api.post('/expert-contexts', payload),
  update: (id, payload) => api.put(`/expert-contexts/${id}`, payload),
  remove: (id) => api.delete(`/expert-contexts/${id}`)
};

// Standalone "Join a Meeting" — push an AI (the tenant's persona) into a Google
// Meet / Zoom PSTN dial-in with NO phone caller bridged in. Admin-only on the server.
export const meetingJoinAPI = {
  join: (payload) => api.post('/meetings/join', payload)
};

// Live listen-in. `live()` is safe to poll; `token()` is owner/admin-only and
// mints a short-lived, subscribe-only credential for one call's room.
export const listenAPI = {
  live: () => api.get('/calls/live'),
  token: (callId) => api.post(`/calls/${callId}/listen-token`, {})
};

export const campaignsAPI = {
  // includeArchived:true also returns soft-hidden (archived) campaigns.
  list: ({ includeArchived = false } = {}) =>
    api.get(`/campaigns${includeArchived ? '?includeArchived=1' : ''}`),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (payload) => api.post('/campaigns', payload),
  update: (id, payload) => api.put(`/campaigns/${id}`, payload),
  setStatus: (id, status) => api.put(`/campaigns/${id}/status`, { status }),
  // Records the campaign's CURRENTLY SAVED behavior block as ear-validated (core
  // copies it into the validated slot and stamps the time). Deliberately its own
  // action rather than a field on the update payload: it asserts that a human
  // listened to a real call on that exact text, which an edit does not.
  // See docs/pitch-authority-architecture-2026-07-28.md.
  validateBehavior: (id) => api.post(`/campaigns/${id}/behavior/validate`),
  // Hide/unhide from the default list without deleting (archived flag).
  setArchived: (id, archived) => api.put(`/campaigns/${id}`, { archived }),
  reset: (id) => api.post(`/campaigns/${id}/reset`),
  remove: (id) => api.delete(`/campaigns/${id}`),
  contacts: (id, params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/campaigns/${id}/contacts${q ? `?${q}` : ''}`);
  },
  addContacts: (id, contacts) => api.post(`/campaigns/${id}/contacts`, { contacts }),
  // Inline per-contact edits, incl. the two call-schedule slots
  // (scheduledAt / scheduledAt2 as ISO strings, or null to clear).
  updateContact: (id, cid, patch) => api.put(`/campaigns/${id}/contacts/${cid}`, patch),
  // BULK status change over a ticked set — one call for the whole set, so a partial
  // failure can't leave half the list changed. `suppress` is what "remove from this
  // list" means: the row, its transcripts and its outcome stay in the campaign and
  // in the report, but the dialer (which claims status 'queued' only) stops calling
  // it. `restore` puts it back; `delete` is a HARD delete with no undo.
  //   → { data: { action, requested, changed, skipped: { notFound, completed, doNotCall, other } } }
  // `completed` and `do_not_call` rows are protected server-side and come back under
  // `skipped` — surface those counts rather than silently changing fewer than ticked.
  // 404 = not deployed yet (see docs/channel-charlie-delta.md, 2026-07-31).
  bulkContacts: (id, { ids, action }) => api.post(`/campaigns/${id}/contacts/bulk`, { ids, action }),
  // FOLLOW-UP CALLBACKS — unlimited per business, each with a required `matter`
  // that becomes that call's goal in the prompt. A FIRED callback is history: the
  // server 409s (`not_pending`) on edit/cancel rather than let it be rewritten.
  // TENANT-WIDE callback queue — "who am I supposed to call back, and when?"
  // Soonest first. Returns campaignStatus so the UI can say a callback will NOT fire
  // (the dialer only walks ACTIVE campaigns), and an `overdue` flag computed server
  // side so every caller isn't re-deriving it against a different clock.
  // 404 = not deployed yet; every surface hides itself.
  callbackQueue: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/callbacks${q ? `?${q}` : ''}`);
  },
  listCallbacks: (id, cid) => api.get(`/campaigns/${id}/contacts/${cid}/callbacks`),
  // Dial MY number through this campaign's exact config. `callbackMatter` rehearses a
  // follow-up instead of the cold pitch; the response echoes `usedCallbackMatter` so the
  // caller can tell whether core actually honoured it (an older core strips it silently).
  testCall: (id, body) => api.post(`/campaigns/${id}/test-call`, body),
  createCallback: (id, cid, payload) => api.post(`/campaigns/${id}/contacts/${cid}/callbacks`, payload),
  updateCallback: (id, cbid, payload) => api.put(`/campaigns/${id}/callbacks/${cbid}`, payload),
  cancelCallback: (id, cbid) => api.delete(`/campaigns/${id}/callbacks/${cbid}`),
  // CALL NOW — one business, immediately. Allowed while manually paused; REFUSED
  // with 409 `campaign_auto_paused` (+ pauseReason) when the health monitor paused
  // the campaign, because dialing through a malfunctioning AI defeats that guard.
  callContactNow: (id, cid, payload) => api.post(`/campaigns/${id}/contacts/${cid}/call`, payload),
  removeContact: (id, cid) => api.delete(`/campaigns/${id}/contacts/${cid}`),
  // Bulk pre-fill the schedule: { start, intervalMinutes, column?, secondOffsetMinutes? }.
  // Or bulk-clear a column: { column, clear: true } nulls that column on all queued contacts.
  autoSchedule: (id, payload) => api.post(`/campaigns/${id}/contacts/auto-schedule`, payload),
  // Schedule a numbered SELECTION of contacts by 1-based createdAt-order index.
  // selection is a string like "1, 40, 33-200". opts (all optional):
  //   { start, intervalMinutes } → stagger the selected entries from `start`,
  //   spaced `intervalMinutes` apart, in # order; { at } or nothing → one time
  //   (call now). { column } picks the call slot (1 or 2). Only the selected
  //   entries are touched. Returns { scheduled, matched, column, spaced }.
  scheduleSelection: (id, selection, opts = {}) =>
    api.post(`/campaigns/${id}/schedule-selection`, { selection, ...opts }),
  // End-of-campaign results report: per-outcome breakdown, headline metrics, and
  // the sales-stage funnel (aggregated server-side from contacts + linked calls).
  report: (id) => api.get(`/campaigns/${id}/report`),
  // Call review: this campaign's calls with full transcript + follow-up outcome.
  calls: (id, params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/campaigns/${id}/calls${q ? `?${q}` : ''}`);
  }
};

// Do-Not-Call list (TCPA / legal opt-out). Tenant-scoped; the server enforces
// admin-only on writes. The dialer scrubs this list before every campaign call.
export const dncAPI = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/dnc${q ? `?${q}` : ''}`);
  },
  add: (payload) => api.post('/dnc', payload),
  // Bulk CSV import — rows is an array of { phoneE164, reason?, notes? }.
  import: (rows) => api.post('/dnc/import', { rows }),
  // phoneE164 is URL-encoded (the leading + must survive the path).
  remove: (phoneE164) => api.delete(`/dnc/${encodeURIComponent(phoneE164)}`)
};

export const listsAPI = {
  list: () => api.get('/lists'),
  get: (id) => api.get(`/lists/${id}`),
  create: (payload) => api.post('/lists', payload),
  update: (id, payload) => api.put(`/lists/${id}`, payload),
  remove: (id) => api.delete(`/lists/${id}`),
  members: (id, params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    ).toString();
    return api.get(`/lists/${id}/members${q ? `?${q}` : ''}`);
  },
  addMembers: (id, contacts) => api.post(`/lists/${id}/members`, { contacts }),
  importToCampaign: (listId, campaignId) =>
    api.post(`/lists/${listId}/import-to-campaign/${campaignId}`)
};

// Public lead capture — no auth header.
export async function submitLead(payload) {
  const base = import.meta.env.VITE_API_URL || '';
  const res = await fetch(`${base.replace(/\/$/, '')}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    let msg = `Submit failed (${res.status})`;
    try { const j = await res.json(); msg = j.error || msg; } catch {}
    throw new Error(msg);
  }
  return res.json();
}

// Public (no auth) — marketing FAQ for the landing page.
export async function fetchPublicFaq() {
  const base = import.meta.env.VITE_API_URL || '';
  const res = await fetch(`${base.replace(/\/$/, '')}/public/faq`);
  if (!res.ok) throw new Error(`Failed to load FAQ (${res.status})`);
  return res.json();
}

// SALES PARTNERS — owner-only line switches for people who dial from the sales portal.
// The portal registers partners (disabled) over its service key; this is where an
// owner opens a line. Server refuses non-owners; every flip is audited.
export const partnersAPI = {
  list: () => api.get('/partners'),
  // Add ahead of their first sign-in; created with the line closed.
  create: (body) => api.post('/partners', body),
  update: (id, body) => api.patch(`/partners/${id}`, body)
};

export const repPhoneAPI = {
  // OUTBOUND: attach to a call THIS REP JUST PLACED, by call id. 409 `no_room` means
  // the room does not exist yet — on box-first it appears a second or two after the
  // dial, well before the prospect answers, so that is "keep waiting", not an error.
  // 409 `call_ended` means stop.
  talkTokenForCall: (callId) => api.post(`/calls/${callId}/talk-token`),

  // INBOUND: attach to whatever is live on one of our LINES, by DID. There is no call
  // id to use — an inbound call writes no Call row until teardown — so the server asks
  // LiveKit which room currently carries that number.
  talkTokenForLine: (did) => api.post('/rep/talk-token', { did }),

  // Is anything live on that line right now? Cheap enough to poll while the rep is at
  // their desk waiting for the phone to ring.
  lineStatus: (did) => api.get(`/rep/line-status?did=${encodeURIComponent(did)}`)
};

// Will Engine subscribers — proxied by CORE, which holds the service key. The browser
// never carries a credential for Will Engine; see core/src/routes/willEngine.js.
export const willEngineAPI = {
  subscribers: (limit = 200) => api.get(`/will-engine/subscribers?limit=${limit}`)
};
